import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { User, ChevronDown, LogOut, Sun, Moon, Eye, X as XIcon, ArrowRightLeft, Search as SearchIcon } from 'lucide-react';
import { useAuthStore, useIsAdmin } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { api, type System, type Agent } from '../lib/api';
import { signOut as cognitoSignOut } from '../lib/cognito';

interface HeaderProps {
  currentSys: System | null;
  switchSystem: (s: System) => void;
}

const SYSTEMS: System[] = ['Messer', 'Empire-ACA'];

export default function Header({ currentSys, switchSystem }: HeaderProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const realUser = useAuthStore((s) => s.realUser);
  const impersonating = useAuthStore((s) => s.impersonating);
  const startImpersonation = useAuthStore((s) => s.startImpersonation);
  const stopImpersonation = useAuthStore((s) => s.stopImpersonation);
  const signOut = useAuthStore((s) => s.signOut);
  const { theme, toggleTheme } = useThemeStore();
  const isAdmin = useIsAdmin();

  // Real-user is admin → load agents for "Login as" dropdown. We only show
  // agents linked to a Cognito user (cognito_sub set) — impersonating an
  // unlinked auto-discovered agent has no meaning since they have no login.
  const agentsQuery = useQuery({
    queryKey: ['agents-all'],
    queryFn: () => api.listAgents(),
    enabled: realUser?.role === 'admin',
  });
  const linkedAgents = (agentsQuery.data?.agents ?? [])
    .filter(
      (a) =>
        a.agent_id !== 'unpaid' && a.agent_id !== 'unknown' && !!a.cognito_sub,
    )
    .sort((a, b) => a.display_name.localeCompare(b.display_name));

  if (!user || !realUser) return null; // header hidden when not signed in

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loginAsOpen, setLoginAsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const loginAsRef = useRef<HTMLDivElement>(null);

  // Click-outside to close dropdowns
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (loginAsRef.current && !loginAsRef.current.contains(e.target as Node)) {
        setLoginAsOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const onImpersonate = (a: Agent) => {
    startImpersonation(a);
    setLoginAsOpen(false);
    setDropdownOpen(false);
    navigate(`/my/dashboard`);
  };

  const onSignOut = () => {
    cognitoSignOut();
    signOut();
    navigate('/login', { replace: true });
  };

  const onStopImpersonation = () => {
    stopImpersonation();
    navigate('/messer/imports');
  };

  return (
    <>
      {impersonating && (
        <div className="bg-amber-500 dark:bg-amber-600 text-white px-4 py-2 text-sm flex items-center justify-center gap-3 shrink-0">
          <Eye size={16} />
          <span>
            Viewing as <strong>{impersonating.display_name}</strong>{' '}
            <span className="opacity-90">
              (signed in as {realUser.display_name.replace(' (admin)', '')})
            </span>
          </span>
          <button
            onClick={onStopImpersonation}
            className="ml-2 px-3 py-1 bg-white/20 hover:bg-white/30 rounded font-medium flex items-center gap-1"
          >
            <XIcon size={14} /> Stop viewing
          </button>
        </div>
      )}

      <header className="topbar h-14 flex items-center justify-between px-3 md:px-5 shrink-0 z-50">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="" className="h-9 w-auto shrink-0" />
            <h1
              className="text-lg font-semibold whitespace-nowrap"
              style={{ color: 'var(--color-topbar-text, var(--color-sidebar-text-active))' }}
            >
              Empire ACA Pay
            </h1>
          </Link>
        </div>

        <div className="flex items-center gap-3">
          {/* Search button (admin only) */}
          {isAdmin && (
            <Link
              to="/search"
              className="btn-icon"
              style={{ color: 'var(--color-topbar-text-muted, #CBD5E1)' }}
              aria-label="Search"
              title="Search policies"
            >
              <SearchIcon size={18} />
            </Link>
          )}

          {/* System switcher (admin only — agents see only their granted systems) */}
          {isAdmin && (
            <div className="flex items-center gap-2">
              <ArrowRightLeft size={14} style={{ color: 'var(--color-topbar-text-muted)' }} />
              <div
                className="flex rounded-lg overflow-hidden"
                style={{ border: '1px solid rgba(255,255,255,0.15)' }}
              >
                {SYSTEMS.map((s) => {
                  const active = currentSys === s;
                  return (
                    <button
                      key={s}
                      onClick={() => switchSystem(s)}
                      className="px-3 py-1.5 text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: active ? 'rgba(34, 211, 238, 0.18)' : 'transparent',
                        color: active
                          ? 'var(--color-topbar-text, #22D3EE)'
                          : 'var(--color-topbar-text-muted, #CBD5E1)',
                      }}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Account dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors"
              style={{ backgroundColor: dropdownOpen ? 'rgba(255,255,255,0.08)' : 'transparent' }}
              aria-haspopup="true"
              aria-expanded={dropdownOpen}
            >
              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                <User size={18} className="text-blue-600 dark:text-blue-400" />
              </div>
              <p
                className="text-sm font-semibold leading-tight hidden sm:block"
                style={{ color: 'var(--color-topbar-text, var(--color-sidebar-text-active))' }}
              >
                {user.display_name}
              </p>
              <ChevronDown
                size={16}
                className={`transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                style={{ color: 'var(--color-topbar-text-muted, var(--color-sidebar-text))' }}
              />
            </button>

            {dropdownOpen && (
              <div
                className="dropdown-menu absolute right-0 mt-2 w-72 rounded-lg shadow-lg py-1 z-50"
                role="menu"
              >
                <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                    {user.display_name}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {user.email || '—'} · {user.role}
                  </p>
                </div>

                {/* Theme toggle */}
                <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: 'var(--color-text)' }}>
                      Theme
                    </span>
                    <div className="flex items-center gap-2">
                      <Sun
                        size={16}
                        className={theme === 'light' ? 'text-yellow-500' : 'text-gray-400'}
                      />
                      <button
                        onClick={toggleTheme}
                        className={`relative w-10 h-5 rounded-full transition-colors duration-300 focus:outline-none ${
                          theme === 'dark' ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                        aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-300 ${
                            theme === 'dark' ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <Moon
                        size={16}
                        className={theme === 'dark' ? 'text-blue-400' : 'text-gray-400'}
                      />
                    </div>
                  </div>
                </div>

                {/* Login as (admin only) */}
                {realUser?.role === 'admin' && (
                  <div className="relative" ref={loginAsRef}>
                    <button
                      onClick={() => setLoginAsOpen((o) => !o)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-2 text-sm transition-colors"
                      style={{ color: 'var(--color-text)' }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = 'var(--color-card-hover)')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = 'transparent')
                      }
                    >
                      <span className="flex items-center gap-3">
                        <Eye size={16} style={{ color: 'var(--color-text-muted)' }} />
                        Login as agent…
                      </span>
                      <ChevronDown
                        size={14}
                        className={`transition-transform ${loginAsOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {loginAsOpen && (
                      <div
                        className="px-2 py-1 max-h-72 overflow-y-auto"
                        style={{ borderTop: '1px solid var(--color-border)' }}
                      >
                        {agentsQuery.isLoading ? (
                          <p
                            className="text-xs px-3 py-2"
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            Loading agents…
                          </p>
                        ) : linkedAgents.length === 0 ? (
                          <p
                            className="text-xs px-3 py-2"
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            No agents have logins yet. Link one on the Users page.
                          </p>
                        ) : (
                          linkedAgents.map((a) => (
                            <button
                              key={a.agent_id}
                              onClick={() => onImpersonate(a)}
                              className="w-full text-left px-3 py-1.5 text-sm rounded transition-colors"
                              style={{ color: 'var(--color-text)' }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  'var(--color-card-hover)')
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor = 'transparent')
                              }
                            >
                              <span className="flex items-center justify-between gap-2">
                                <span>{a.display_name}</span>
                                <span
                                  className="text-xs"
                                  style={{ color: 'var(--color-text-muted)' }}
                                >
                                  {a.system_access?.join(', ')}
                                </span>
                              </span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="py-1">
                  <button
                    onClick={onSignOut}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 transition-colors"
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = 'transparent')
                    }
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
